/**
 * Reference data slugs and citation slugs must be globally unique across
 * Watershed. We use the last 10 characters of an orgId as its unique suffix for
 * reference data and citations.
 *
 * ID generation code looks like this:
 *   entropy = ''.join(
 *     [random.choice(helpers.BASE_58_ALPHABET) for i in range(0, 10)]
 *   )
 *   return f"{prefix}_{formatted_generation}{shard}{formatted_age}{entropy}"
 */

import { UnexpectedError } from '@watershed/errors/UnexpectedError';

const ORG_SUFFIX_LENGTH = 10;
// with an underscore
const SUFFIX_LENGTH = ORG_SUFFIX_LENGTH + 1;

/**
 * Get the suffix for a given orgId.
 */
export function getOrgSuffix(orgId: string): string {
  if (!orgId.startsWith('org_') || orgId.length !== 24) {
    throw new UnexpectedError("Can't get suffix for invalid orgId", {
      data: { orgId },
    });
  }

  return `_${orgId.slice(-ORG_SUFFIX_LENGTH).toLowerCase()}`;
}

/**
 * Validate that a slug has the right org suffix.
 */
export function hasValidOrgSuffix({
  orgId,
  slug,
}: {
  orgId: string;
  slug: string;
}): boolean {
  return getOrgSuffix(orgId) === slug.slice(-SUFFIX_LENGTH);
}

/**
 * Remove the org suffix from a slug and return the first part of the slug.
 */
export function removeOrgSuffixFromSlug({
  orgId,
  slug,
}: {
  orgId: string;
  slug: string;
}): string {
  if (!hasValidOrgSuffix({ orgId, slug })) {
    throw new UnexpectedError("Can't remove invalid org suffix from slug", {
      data: { orgId, slug },
    });
  }

  return slug.slice(0, -SUFFIX_LENGTH);
}
